import { Injectable } from '@angular/core';
import {
  camelCase,
  compact,
  find,
  has,
  includes,
  join,
  reduce,
  reject,
  toString,
} from 'lodash';

import { ApplicationConfig } from '@lockton-apps/shared/interfaces';

import { FormlyFieldConfigWithDisplayViewOptions } from '../interfaces';
import { PresentationBuilderAdapter } from './presentation-builder-adapter.service';
import {
  PBArrayConfig,
  PBFieldConfig,
  PBGroupConfig,
  PBHeadingConfig,
  PBConfigItem,
  PBConfigItemType,
} from '../interfaces/presentation-builder-config.model';

/** Convert proposal form config into presentation builder items  */
@Injectable({
  providedIn: 'root',
})
export class DynamicFormsPresentationAdapter extends PresentationBuilderAdapter<
  ApplicationConfig,
  FormlyFieldConfigWithDisplayViewOptions
> {
  read(
    config: ApplicationConfig
  ): PBConfigItem<FormlyFieldConfigWithDisplayViewOptions>[] {
    return reduce(
      config.steps,
      (acc, step) => {
        const stepId = camelCase(step.id);

        const inner = reduce(
          step.fieldGroups,
          (accStep, fieldGroup) => {
            const path = [stepId, fieldGroup.id];

            const heading = fieldGroup.label
              ? [
                  this.buildHeading(
                    [stepId],
                    fieldGroup.id,
                    fieldGroup.label,
                    3
                  ),
                ]
              : [];

            const flattenedFields = this.flattenAbstractGroupFields(
              fieldGroup.fields
            );

            const fields = this.fieldGroupDisplayViewData(
              path,
              flattenedFields
            );

            return [...accStep, ...heading, ...fields];
          },
          [
            this.buildHeading([], stepId, step.label, 1),
          ] as PBConfigItem<FormlyFieldConfigWithDisplayViewOptions>[]
        );

        return [...acc, ...inner];
      },
      [] as PBConfigItem<FormlyFieldConfigWithDisplayViewOptions>[]
    );
  }

  private fieldGroupDisplayViewData(
    basePath: string[],
    fields: FormlyFieldConfigWithDisplayViewOptions[]
  ) {
    return reduce(
      fields,
      (acc, field) => {
        // Ignore template fields
        if (
          field.type === 'template' ||
          has(field, 'template') ||
          field.type === 'hidden' ||
          field.type === 'divider' ||
          field.displayViewOptions?.omit
        ) {
          return acc;
        }

        let newAcc = [...acc];

        if (field.displayViewOptions?.groupBy) {
          const group = find(newAcc, {
            groupId: field.displayViewOptions?.groupBy,
          });

          if (group && group.type === 'group') {
            group.fields.push(field);
          } else {
            newAcc = [...acc, this.createGroup(basePath, field)];
          }
        } else {
          if (field.type === 'template') {
            return newAcc;
          }

          if (field.type === 'dynamic') {
            newAcc = [...acc, this.createArray(basePath, field)];
            return newAcc;
          }

          newAcc = [...newAcc, this.createField(basePath, field)];
        }

        return newAcc;
      },
      [] as PBConfigItem<FormlyFieldConfigWithDisplayViewOptions>[]
    );
  }

  private buildHeading(
    basePath: string[],
    id: string,
    value: string,
    level: number
  ): PBHeadingConfig {
    const path = compact([...basePath, id]);
    return {
      id: this.createId(path),
      path,
      pathString: join(path, '.'),
      type: PBConfigItemType.HEADING,
      value,
      level,
    };
  }

  private createField(
    basePath: string[],
    field: FormlyFieldConfigWithDisplayViewOptions
  ): PBFieldConfig<FormlyFieldConfigWithDisplayViewOptions> {
    const path = compact([...basePath, toString(field.key)]);
    return {
      id: this.createId(path),
      path,
      pathString: join(path, '.'),
      type: PBConfigItemType.FIELD,
      field: field,
    };
  }

  private createGroup(
    basePath: string[],
    field: FormlyFieldConfigWithDisplayViewOptions
  ): PBGroupConfig<FormlyFieldConfigWithDisplayViewOptions> {
    return {
      id: this.createId(basePath),
      groupId: field.displayViewOptions?.groupBy as string,
      path: basePath,
      pathString: join(basePath, '.'),
      type: PBConfigItemType.GROUP,
      fields: this.rejectHiddenFields([field]),
    };
  }

  private createArray(
    basePath: string[],
    field: FormlyFieldConfigWithDisplayViewOptions
  ): PBArrayConfig<FormlyFieldConfigWithDisplayViewOptions> {
    if (!field.props || !field.props['fields']) {
      throw new Error(
        `Dynamic field ${field.key} does not have any fields defined`
      );
    }

    const path = [...basePath, field.key as string];

    return {
      id: this.createId(path),
      arrayId: field.key as string,
      path,
      pathString: join(path, '.'),
      type: PBConfigItemType.ARRAY,
      fields: this.rejectHiddenFields(field.props['fields']),
      typeOverride: field.displayViewOptions?.typeOverride,
      props: field.displayViewOptions?.props,
    };
  }

  private rejectHiddenFields(
    fields: FormlyFieldConfigWithDisplayViewOptions[]
  ) {
    return reject(
      fields,
      (field) =>
        includes(['hidden', 'divider', 'template'], field.type) ||
        !!field.displayViewOptions?.omit
    );
  }

  private createId(path: string[]) {
    return join(path, '-');
  }

  private flattenAbstractGroupFields(
    fields: Array<
      FormlyFieldConfigWithDisplayViewOptions & {
        fields: FormlyFieldConfigWithDisplayViewOptions[];
      }
    >
  ) {
    return reduce(
      fields,
      (acc, field) => {
        if (field.type === 'abstractGroup') {
          return [...acc, ...field.fields];
        }
        return [...acc, field];
      },
      [] as FormlyFieldConfigWithDisplayViewOptions[]
    );
  }
}
